import React, { useState, ReactNode } from "react";

// Define the shape of each tab
export interface Tab {
 title: string;
 content: ReactNode;
 headerAction?: ReactNode; // Optional action button for tab header
}

// Define the props for the Tabs component
interface TabsProps {
 tabs: Tab[];
 tabsKey: string;
 orientation?: "horizontal" | "vertical";
 sticky?: boolean; // Make tabs sticky
 contentPadding?: boolean; // Whether to add padding to tab content (default: true)
 renderHeadersOnly?: boolean; // If true, only render headers (for use in Modal)
 onTabChange?: (index: number) => void; // Callback when tab changes
 activeTab?: number; // Controlled active tab (when provided, component becomes controlled)
}

const Tabs: React.FC<TabsProps> = ({
 tabs,
 tabsKey,
 orientation = "horizontal",
 sticky = false,
 contentPadding = true,
 renderHeadersOnly = false,
 onTabChange,
 activeTab: controlledActiveTab,
}) => {
 const [internalActiveTab, setInternalActiveTab] = useState(0);
 const activeTab = controlledActiveTab !== undefined ? controlledActiveTab : internalActiveTab;

 const handleTabChange = (index: number) => {
  if (controlledActiveTab === undefined) {
   setInternalActiveTab(index);
  }
  onTabChange?.(index);
 };

 if (orientation === "vertical") {
  return (
   <div className="relative w-full flex flex-row gap-6">
    {/* Vertical Tab Headers */}
    <div className="flex flex-col min-w-[180px] border-r bg-gray-50 py-2">
     {tabs.map((tab, index) => (
      <button
       key={`tabs-${tabsKey}-${index}`}
       type="button"
       onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleTabChange(index);
       }}
       className={`py-2 px-4 text-left font-semibold transition-colors mb-1 ${
        activeTab === index
         ? "bg-blue-100 text-blue-700 shadow"
         : "text-gray-600 hover:bg-gray-100"
       }`}
      >
       {tab.title}
      </button>
     ))}
    </div>
    {/* Tab Content */}
    <div className="flex-1 p-0">
     {tabs[activeTab] && (
      <div className="text-gray-700">{tabs[activeTab].content}</div>
     )}
    </div>
   </div>
  );
 }

 // Horizontal tabs (default)
 return (
  <div className="w-full mx-auto">
   {/* Tab Headers */}
   <div
    className={`flex items-center justify-between space-x-4 border-b bg-white ${
     sticky ? "sticky top-0 z-10" : ""
    }`}
   >
    <div className="flex space-x-4">
     {tabs.map((tab, index) => (
      <button
       key={`tabs-${tabsKey}-${index}`}
       type="button"
       onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleTabChange(index);
       }}
       className={`py-2 px-4 font-semibold transition-colors ${
        activeTab === index
         ? "border-b-2 border-blue-500 text-blue-500"
         : "text-gray-500 hover:text-blue-500"
       }`}
      >
       {tab.title}
      </button>
     ))}
    </div>
    {/* Tab Header Action - shown when active tab has headerAction */}
    {tabs[activeTab]?.headerAction && (
     <div className="flex items-center">
      {tabs[activeTab].headerAction}
     </div>
    )}
   </div>

   {/* Tab Content */}
   {!renderHeadersOnly && (
    <div className={contentPadding ? "p-4" : ""}>
     {tabs[activeTab] && (
      <div className="text-gray-700">{tabs[activeTab].content}</div>
     )}
    </div>
   )}
  </div>
 );
};

export default Tabs;
